import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
// components
import { PatternFormat } from 'react-number-format';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Form from 'react-bootstrap/Form';
import { Spinner } from '../../../../../components/Spinner';
// API
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';

function NewFundingWithdrawalsWidget({
  fundingInitialAmount,
  fundingTopups,
  consumerId,
  refreshFunding,
}) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginFunding, apiOriginConsumer, apiOriginMonitor } = useEnv();

  const [spinner, setSpinner] = useState(false);
  const [isManualTopupOpen, setIsManualTopupOpen] = useState(false);

  const [amount, setAmount] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const [prediction, setPrediction] = useState(null);

  const getPrediction = useCallback(async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginMonitor}/get_prediction_admin/v2`,
      method: 'GET',
      params: { consumerId },
    };

    try {
      const response = await makeRequest(config);

      setPrediction(response);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  }, [getAccessTokenSilently, apiOriginMonitor, consumerId]);

  useEffect(() => {
    if (isManualTopupOpen) {
      getPrediction();
    }
  }, [isManualTopupOpen, getPrediction]);

  const handleToggleManualTopup = () => {
    setIsManualTopupOpen(prev => !prev);
    setAmount(prediction?.activeOffer ? prediction.activeOffer.allAmounts[0] : '');
    setMfaCode('');
  };

  const sendMfa = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/mfa/send`,
      method: 'POST',
      data: JSON.stringify({ consumerId, mfaUseCase: 'ADVANCE' }),
    };

    try {
      const response = await makeRequest(config);

      toast.success(`MFA code has been sent by ${response.mfaChannel} to ${response.mfaMask}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const createManualTopup = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/create_manual`,
      method: 'POST',
      data: JSON.stringify({
        cosnumerId: consumerId,
        offerId: prediction.activeOffer ? prediction.activeOffer.offerId : null,
        requestedAmount: parseInt(amount, 10),
        textualReason: '',
        type: 'TOPUP',
        topupFundingId: null,
        mfaCode,
        consent: true,
      }),
    };

    try {
      await makeRequest(config);
      await refreshFunding();

      toast.success('Top-up created successfully!');
      handleToggleManualTopup();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <div className="bo-border-container bg-light rounded w-100 p-0">
      <div className="p-2">
        <div className="d-flex justify-content-between pb-1">
          <p className="bo-common-text-bold">Withdrawals</p>
          <OverlayTrigger
            trigger="click"
            placement="bottom-start"
            show={isManualTopupOpen}
            popperConfig={{
              modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
            }}
            overlay={
              <Popover className="custom-popover">
                <Popover.Body className="bo-common-popover-form-menu">
                  <Spinner visible={spinner} size={36}>
                    <Form.Label className="bo-common-popover-form-menu-label">Amount</Form.Label>
                    {prediction?.activeOffer ? (
                      <Form.Select
                        className="bo-common-popover-form-menu-input bo-w-150"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                      >
                        {prediction.activeOffer.allAmounts.map(amt => (
                          <option key={amt} value={amt}>
                            ${amt}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type="text"
                        className="bo-common-popover-form-menu-input bo-w-150"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                      />
                    )}
                    <Form.Label className="bo-common-popover-form-menu-label mt-2">
                      MFA Code
                    </Form.Label>
                    <div className="d-flex gap-3 bo-w-200">
                      <PatternFormat
                        className="bo-common-popover-form-menu-input bo-w-150"
                        type="text"
                        value={mfaCode || ''}
                        customInput={Form.Control}
                        format="######"
                        mask="_"
                        onChange={e => setMfaCode(e.target.value)}
                      />
                      <Button
                        className="bo-common-inline-button text-decoration-none"
                        disabled={spinner}
                        onClick={sendMfa}
                      >
                        Send
                      </Button>
                    </div>
                    <div className="mt-3 d-flex justify-content-between">
                      <Button
                        className="bo-common-button"
                        onClick={createManualTopup}
                        disabled={
                          spinner || !amount || !mfaCode || mfaCode.replace('_', '').length !== 6
                        }
                      >
                        Create
                      </Button>
                      <Button className="bo-common-button" onClick={handleToggleManualTopup}>
                        Close
                      </Button>
                    </div>
                  </Spinner>
                </Popover.Body>
              </Popover>
            }
          >
            <Button className="bo-common-button" onClick={handleToggleManualTopup}>
              Manual Top-up
            </Button>
          </OverlayTrigger>
        </div>
        <div className="d-flex justify-content-between pb-1">
          <p className="bo-common-text">Initial Amount</p>
          <p className="bo-common-text">${fundingInitialAmount}</p>
        </div>
        {fundingTopups.length > 0 && (
          <div className="d-flex justify-content-between pb-1">
            <p className="bo-common-text">Top-ups</p>
            <div className="d-flex flex-column align-items-end">
              {fundingTopups.map(x => (
                <p key={x.topupId} className="bo-common-text">
                  {`$${x.amount} on ${moment(x.timestamp).format('YYYY-MM-DD')}`}
                </p>
              ))}
            </div>
          </div>
        )}
        <div className="d-flex justify-content-between">
          <p className="bo-common-text-bold">Total Amount</p>
          <p className="bo-common-text-bold">
            {`$${fundingInitialAmount + fundingTopups.reduce((acc, x) => acc + x.amount, 0)}`}
          </p>
        </div>
      </div>
    </div>
  );
}

export default NewFundingWithdrawalsWidget;
